package com.essntl.features.itinerary.ui.edit_itinerary_service.components

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import core.dateformat.utils.isNow
import com.essntl.features.itinerary.domain.use_case.UpdateItineraryServiceUseCase
import com.essntl.features.itinerary.ui.edit_itinerary.MessageSendType
import com.essntl.features.itinerary.ui.edit_itinerary.components.ItineraryServiceStatusDropDown
import com.essntl.features.itinerary.ui.edit_itinerary_service.EditItineraryServiceEvent
import com.essntl.features.itinerary.ui.edit_itinerary_service.EditItineraryServiceNavigation
import com.essntl.features.itinerary.ui.edit_itinerary_service.EditItineraryServiceState
import com.essntl.features.supplier.ui.ContactSupplierPopup
import com.essntl.uikit.components.button.primary.EsMediumPrimaryButton
import com.essntl.uikit.components.button.secondary.EsMediumSecondaryButton
import com.essntl.uikit.components.button.tinted.EsLargeTintedButton
import com.essntl.uikit.components.iconbutton.outline.EsSmallOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.tooltip.EsTooltip
import com.essntl.uikit.components.utils.EsRowDefaultItem
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import org.koin.compose.koinInject

@Composable
fun EditItineraryServiceHeader(
    state: EditItineraryServiceState,
    onEvent: (EditItineraryServiceEvent) -> Unit,
    onNavigation: (EditItineraryServiceNavigation) -> Unit,
) {
    val updateItineraryServiceUseCase = koinInject<UpdateItineraryServiceUseCase>()

    val itineraryService = state.itineraryService.value

    var isContactSupplierPopupOpen by remember { mutableStateOf(false) }

    LaunchedEffect(updateItineraryServiceUseCase) {
        updateItineraryServiceUseCase.successEvent.collect {
            if (it.item.poSentAt != null && !isNow(it.item.poSentAt))
                isContactSupplierPopupOpen = true
        }
    }

    Column(
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.Start,
        modifier = Modifier
            .fillMaxWidth()
            .clip(
                RoundedCornerShape(
                    bottomEnd = 24.dp,
                    bottomStart = 24.dp,
                ),
            )
            .padding(horizontal = 2.5.spacing)
            .padding(bottom = 1.spacing),
    ) {
        Row(
            verticalAlignment = Alignment.Top,
            horizontalArrangement = Arrangement.Start,
        ) {
            // Status
            EsRowDefaultItem(
                text = "",
                extraContent = {
                    itineraryService.status?.let {
                        ItineraryServiceStatusDropDown(
                            status = it,
                            onStatusSelected = { newStatus ->
                                if (newStatus != itineraryService.status)
                                    onEvent(
                                        EditItineraryServiceEvent.UpdateItineraryStatus(
                                            itineraryService = itineraryService,
                                            status = newStatus,
                                        ),
                                    )
                            },
                        )
                    }
                },
                isFirst = true,
            )

            Spacer(Modifier.weight(1F))

            // Select Supplier
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.Center,
            ) {
                SelectionContainer {
                    Text(
                        text = itineraryService.supplier?.prettyName ?: "No supplier attached",
                        color = MaterialTheme.esColorScheme.textTertiary,
                        style = MaterialTheme.esTypography.p1Regular16,
                        textAlign = TextAlign.End,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier
                            .fillMaxWidth(0.5F),
                    )
                }
                Spacer(modifier = Modifier.width(width = 1.5.spacing))

                EsSmallOutlineIconButton(
                    iconRes = EsIcons.Edit,
                    onClick = { onNavigation(EditItineraryServiceNavigation.ChangeSupplier) },
                )
            }
        }

        Spacer(modifier = Modifier.height(1.5.spacing))

        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center,
            modifier = Modifier.fillMaxWidth(),
        ) {
            val purchaseOrderEnabled = itineraryService.supplier != null

            itineraryService.supplier?.contact?.organization?.website?.let { website ->
                val uriHandler = LocalUriHandler.current

                EsTooltip(
                    text = "Visit $website",
                    content = {
                        EsLargeTintedButton(
                            text = "🔗",
                            onClick = {
                                try {
                                    uriHandler.openUri(
                                        uri = website,
                                    )
                                } catch (exception: Exception) {
                                    // Handle the exception here
                                    println("Wrong URL")
                                }
                            },
                        )
                    },
                )
                Spacer(modifier = Modifier.width(width = 2.5.spacing))
            }

            EsMediumPrimaryButton(
                onClick = {
                    onNavigation(
                        EditItineraryServiceNavigation.SendMessage(
                            type = MessageSendType.SUPPLIER_PO,
                        ),
                    )
                },
                text = "Purchase Order",
                enabled = purchaseOrderEnabled,
                modifier = Modifier.weight(1F),
            )
            Spacer(modifier = Modifier.width(width = 2.5.spacing))
            EsMediumSecondaryButton(
                onClick = {
                    onNavigation(
                        EditItineraryServiceNavigation.SendMessage(
                            type = MessageSendType.SUPPLIER_GENERIC,
                        ),
                    )
                },
                text = "Contact supplier",
                enabled = itineraryService.supplier != null,
                modifier = Modifier.weight(1F),
            )
        }

        Spacer(modifier = Modifier.height(2.spacing))

        LastUpdatedLabel(state = state)

        ContactSupplierPopup(
            isOpen = isContactSupplierPopupOpen,
            onDismissRequest = {
                isContactSupplierPopupOpen = false
            },
            openMessagePopup = {
                isContactSupplierPopupOpen = false
                onNavigation(
                    EditItineraryServiceNavigation.SendMessage(
                        type = MessageSendType.SUPPLIER_PO,
                    ),
                )
            },
        )
    }
}